<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :show-close-button="true"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :shading="true"
    :width="600"
    height="auto"
    :title="costPopupTitle"
    @hiding="onClose"
  >
    <div class="cost-popup-body">
      <div class="row">
        <div class="form-row col-sm-12 col-lg-6">
          <label class="control-label">{{ $t("works.work_machines.cost_date") }}</label>
          <DxDateBox
            :key="`date-${formKey}`"
            v-model="costForm.date"
            type="date"
            :use-mask-behavior="true"
            :display-format="displayDateFormat"
            :class="inputClass('date')"
          />
          <small v-if="errors.date" class="text-danger">{{ errors.date }}</small>
        </div>
        <div class="form-row col-sm-12 col-lg-6">
          <label class="control-label">{{ $t("works.work_machines.cost_type") }}</label>
          <DxSelectBox
            :key="`type-${formKey}`"
            :search-enabled="true"
            v-model="costForm.cost_type_code"
            :data-source="workMachineCostTypes"
            value-expr="code"
            display-expr="label"
            :placeholder="$t('works.work_machines.cost_type_placeholder')"
            :class="inputClass('cost_type_code')"
          />
          <small v-if="errors.cost_type_code" class="text-danger">{{ errors.cost_type_code }}</small>
        </div>
      </div>
      <div class="row">
        <div class="form-row col-sm-12">
          <label class="control-label">{{ $t("common.name") }}</label>
          <DxTextBox :key="`name-${formKey}`" v-model="costForm.name" :class="inputClass('name')" />
          <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
        </div>
      </div>
      <div class="row">
        <div class="form-row col-sm-12 col-lg-6">
          <label class="control-label">{{ $t("common.unit_price") }}</label>
          <DxNumberBox
            :key="`unit_price-${formKey}`"
            v-model="costForm.unit_price"
            :min="0"
            :show-spin-buttons="false"
            @value-changed="onUnitPriceChange"
            :format="this.getDxGridDefaultNumberFormat"
            :class="inputClass('unit_price')"
          />
          <small v-if="errors.unit_price" class="text-danger">{{ errors.unit_price }}</small>
        </div>
        <div class="form-row col-sm-12 col-lg-6">
          <label class="control-label">{{ $t("common.quantity") }}</label>
          <DxNumberBox
            :key="`quantity-${formKey}`"
            v-model="costForm.quantity"
            :min="0"
            :show-spin-buttons="false"
            @value-changed="onQuantityChange"
            :format="this.getDxGridDefaultNumberFormat"
            :class="inputClass('quantity')"
          />
          <small v-if="errors.quantity" class="text-danger">{{ errors.quantity }}</small>
        </div>
      </div>
      <div class="row">
        <div class="form-row col-sm-12 col-lg-6">
          <label class="control-label">{{ $t("common.value") }}</label>
          <DxNumberBox
            :key="`value-${formKey}`"
            v-model="costForm.value"
            :read-only="false"
            :format="this.getDxGridDefaultNumberFormat"
            @value-changed="onValueChange"
            :class="inputClass('value')"
          />
          <small v-if="errors.value" class="text-danger">{{ errors.value }}</small>
        </div>
        <div class="form-row col-sm-12 col-lg-6">
          <label class="control-label">{{ $t("common.notes") }}</label>
          <DxTextArea v-model="costForm.notes" />
        </div>
      </div>
      <div class="action-btt text-right">
        <SmagriaButton
          :label="$t('works.work_machines.save_cost')"
          :disabled="loading"
          :action="onSave"
          :customClass="'primary'"
          :width="'170px'"
        />
        <SmagriaButton
          :label="$t('common.cancel')"
          :disabled="loading"
          :action="onClose"
          :customClass="'secondary border margin-left'"
          :width="'130px'"
        />
      </div>
    </div>
  </DxPopup>
</template>

<script>
import DxPopup from "devextreme-vue/popup";
import DxDateBox from "devextreme-vue/date-box";
import DxSelectBox from "devextreme-vue/select-box";
import DxTextBox from "devextreme-vue/text-box";
import DxNumberBox from "devextreme-vue/number-box";
import DxTextArea from "devextreme-vue/text-area";
import { mapActions, mapGetters } from "vuex";
import { format } from "date-fns";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";

export default {
  name: "WorkMachineCostPopup",
  components: {
    DxPopup,
    DxDateBox,
    DxSelectBox,
    DxTextBox,
    DxNumberBox,
    DxTextArea,
    SmagriaButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    machine: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dateFormat: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      costForm: this.getEmptyForm(),
      costTypesLoading: false,
      costUpdateLock: false,
      errors: {},
      formKey: 0,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetForm();
        this.loadCostTypes();
      }
    },
    machine: {
      handler() {
        if (this.visible) {
          this.resetForm();
        }
      },
      deep: false,
    },
  },
  computed: {
    ...mapGetters({
      workMachineCostTypes: "works-work-machines/workMachineCostTypes",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      getDxGridDefaultNumberFormat: "settings/getDxGridDefaultNumberFormat",
    }),
    displayDateFormat() {
      return this.dateFormat || this.getDxGridDefaultDateFormat || "yyyy-MM-dd";
    },
    costPopupTitle() {
      const base = this.$t("works.work_machines.add_cost");
      const name = this.machine?.name;
      return name ? `${base} - ${name}` : base;
    },
  },
  methods: {
    ...mapActions({
      getWorkMachineCostTypes: "works-work-machines/getWorkMachineCostTypes",
    }),
    getEmptyForm() {
      return {
        machine_uuid: null,
        date: new Date(),
        cost_type_code: null,
        name: "",
        unit_price: 0,
        quantity: 1,
        value: 0,
        notes: "",
      };
    },
    resetForm() {
      const base = this.getEmptyForm();
      base.machine_uuid = this.machine?.uuid || null;
      this.costForm = base;
      this.errors = {};
      this.formKey += 1;
    },
    async loadCostTypes() {
      if (this.costTypesLoading) return;
      if (this.workMachineCostTypes && this.workMachineCostTypes.length > 0) return;
      this.costTypesLoading = true;
      try {
        await this.getWorkMachineCostTypes();
      } catch (e) {
        // swallow; error surfaced via global handler
      } finally {
        this.costTypesLoading = false;
      }
    },
    num(value) {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    },
    withCostLock(cb) {
      if (this.costUpdateLock) return;
      this.costUpdateLock = true;
      try {
        cb();
      } finally {
        this.costUpdateLock = false;
      }
    },
    validateForm() {
      const errors = {};
      if (!this.costForm.date) errors.date = this.$t("common.required") || "Required";
      if (!this.costForm.cost_type_code) errors.cost_type_code = this.$t("common.required") || "Required";
      if (!this.costForm.name || !String(this.costForm.name).trim()) errors.name = this.$t("common.required") || "Required";
      const qty = this.num(this.costForm.quantity);
      const price = this.num(this.costForm.unit_price);
      const val = this.num(this.costForm.value);
      if (qty <= 0) errors.quantity = this.$t("common.required") || "Required";
      if (price <= 0) errors.unit_price = this.$t("common.required") || "Required";
      if (val <= 0) errors.value = this.$t("common.required") || "Required";
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    inputClass(field) {
      return { "field-error": !!this.errors[field] };
    },
    onUnitPriceChange() {
      this.withCostLock(() => {
        const price = this.num(this.costForm.unit_price);
        const qty = this.num(this.costForm.quantity);
        this.costForm.value = price * qty;
      });
    },
    onQuantityChange() {
      this.withCostLock(() => {
        const price = this.num(this.costForm.unit_price);
        const qty = this.num(this.costForm.quantity);
        this.costForm.value = price * qty;
      });
    },
    onValueChange() {
      this.withCostLock(() => {
        const val = this.num(this.costForm.value);
        const qty = this.num(this.costForm.quantity);
        if (qty > 0) {
          this.costForm.unit_price = val / qty;
        }
      });
    },
    formatDateForApi(value) {
      if (!value) return null;
      try {
        return format(new Date(value), "yyyy-MM-dd");
      } catch (e) {
        return null;
      }
    },
    onSave() {
      if (!this.costForm.machine_uuid) return;
      if (!this.validateForm()) return;
      const payload = {
        ...this.costForm,
        date: this.formatDateForApi(this.costForm.date),
      };
      this.$emit("save-cost", payload);
    },
    onClose() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/global/colors.scss";

.cost-popup-body .form-row {
  margin-bottom: 12px;
}

::v-deep(.field-error .dx-texteditor-input) {
  border-color: $danger-color;
}

::v-deep(.field-error .dx-numberbox-spin-container) {
  border-color: $danger-color;
}

.action-btt {
  margin-top: 24px;
}
</style>
